<!-- The exported code uses Tailwind CSS. Install Tailwind CSS in your dev environment to ensure all styles work. -->

<template>
  <div class="app-container" @click="closePublishOptions">

    <!-- 当前显示的页面 -->
    <div class="page-container max-w-screen-lg mx-auto overflow-y-auto" >
      <div class="bg-gray-50 min-h-screen pb-16">
        <!-- 顶部导航栏 -->
        <div class="nav-bar fixed top-0 w-full bg-white shadow-sm z-50">
          <!-- 搜索栏 -->
          <div class="px-4 py-2 bg-white">
            <div class="relative max-w-screen-md mx-auto">
              <input
                type="text"
                placeholder="搜索二维码"
                v-model="searchName"
                @input="handleSearchInput"
                @keyup.enter="handleSearchInput"
                @blur="handleSearchInput"
                inputmode="text"
                class="w-full py-2 pl-10 pr-4 bg-gray-100 border-none rounded-full text-sm focus:outline-none"
              />
              <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            </div>
          </div>
          <!-- 分类标签栏 -->
          <div class="category-tabs overflow-x-auto whitespace-nowrap px-2 py-2 bg-white border-b border-gray-100">
            <div class="inline-flex max-w-screen-md mx-auto">
              <div v-for="(category, index) in categories" :key="index" :class="['px-4 py-2 mx-1 text-sm font-medium rounded-full cursor-pointer',
                activeCategory === index ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600']"
                @click="setActiveCategory(index)">
                {{ category }}
              </div>
            </div>
          </div>
          <!-- 筛选选项 -->
          <div class="filter-options flex justify-between px-4 py-2 bg-white border-b border-gray-100 max-w-screen-md mx-auto">
            <div v-for="(filter, index) in filters" :key="index" :class="['text-sm cursor-pointer flex items-center',
              activeFilter === index ? 'text-blue-500 font-medium' : 'text-gray-600']" @click="setActiveFilter(index)">
              {{ filter.name }}
              <i :class="['ml-1 fas', filter.icon]"></i>
            </div>
          </div>
        </div>
        <!-- 内容区域 -->
        <div class="content-area pt-44 px-3 pb-4 max-w-screen-md mx-auto overflow-y-auto"
          :style="{ height: `calc(100vh)` }"
          @scroll="handleScroll">
          <!-- 二维码卡片网格 -->
          <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            <div v-for="(qrCode, index) in qrCodes" :key="index"
              class="qr-card bg-white rounded-lg overflow-hidden shadow-sm cursor-pointer" @click="viewQRCode(qrCode)">
              <div class="relative">
                <img :src="qrCode.image" :alt="qrCode.name" class="w-full h-40 object-cover object-top" />
                <div class="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                  {{ qrCode.type }}
                </div>
              </div>
              <div class="p-3">
                <h3 class="text-sm font-medium text-gray-800 truncate">{{ qrCode.name }}</h3>
                <p class="text-xs text-gray-500 mt-1 line-clamp-2 h-8">{{ qrCode.description }}</p>
                <div class="flex items-center justify-between mt-2">
                  <div class="flex items-center">
                    <img :src="qrCode.userAvatar" :alt="qrCode.userName" class="w-5 h-5 rounded-full object-cover" />
                    <span class="text-xs text-gray-600 ml-1 truncate max-w-[70px]">{{ qrCode.userName }}</span>
                  </div>
                  <div class="flex items-center">
                    <i class="fas fa-eye text-xs text-gray-400"></i>
                    <span class="text-xs text-gray-400 ml-1">{{ qrCode.views }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 发布按钮 -->
        <div class="fixed right-8 bottom-8 z-50" @click.stop>
          <button
            class="w-14 h-14 bg-blue-500 rounded-full shadow-lg flex items-center justify-center text-white cursor-pointer !rounded-button"
            @click="togglePublishOptions">
            <i class="fas fa-plus text-xl"></i>
          </button>
          <!-- 发布选项 -->
          <div v-if="showPublishOptions"
            class="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl w-40 overflow-hidden">
            <div v-for="(option, index) in publishOptions" :key="index"
              class="flex items-center p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100"
              @click="selectPublishOption(index)">
              <i :class="['fas', option.icon, 'text-blue-500 w-6']"></i>
              <span class="ml-2 text-sm">{{ option.name }}</span>
            </div>
          </div>
          <!-- 上传二维码对话框 -->
          <div v-if="showUploadDialog" class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div class="bg-white rounded-lg w-full max-w-md p-6">
              <h3 class="text-lg font-medium text-gray-800 mb-4">上传二维码</h3>
              <form @submit.prevent="submitQRCode">
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700">名称</label>
                  <input v-model="newQRCode.name" type="text" class="w-full mt-1 p-2 border rounded" required />
                  <p v-if="!newQRCode.name && showValidationErrors" class="text-red-500 text-xs mt-1">名称不能为空</p>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700">类型</label>
                  <select v-model="newQRCode.type" class="w-full mt-1 p-2 border rounded" required>
                    <option value="" disabled>请选择类型</option>
                    <option v-for="category in filteredCategories" :key="category" :value="category">
                      {{ category }}
                    </option>
                  </select>
                  <p v-if="!newQRCode.type && showValidationErrors" class="text-red-500 text-xs mt-1">类型不能为空</p>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700">描述</label>
                  <textarea v-model="newQRCode.description" class="w-full mt-1 p-2 border rounded" rows="3" required></textarea>
                  <p v-if="!newQRCode.description && showValidationErrors" class="text-red-500 text-xs mt-1">描述不能为空</p>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700">图片</label>
                  <input type="file" @change="handleImageUpload" class="w-full mt-1 p-2 border rounded" required />
                  <p v-if="!newQRCode.image && showValidationErrors" class="text-red-500 text-xs mt-1">图片不能为空</p>
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700">上传者微信号(用于联系客服下架二维码的身份验证)</label>
                  <input v-model="newQRCode.userName" type="text" class="w-full mt-1 p-2 border rounded" required />
                  <p v-if="!newQRCode.userName && showValidationErrors" class="text-red-500 text-xs mt-1">上传者微信号不能为空</p>
                </div>
                <div class="flex justify-end">
                  <button type="button" class="px-4 py-2 bg-gray-300 rounded mr-2" @click="closeUploadDialog">取消</button>
                  <button type="submit" class="px-4 py-2 bg-blue-500 text-white rounded">提交</button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <!-- 二维码详情弹窗 -->
        <div v-if="selectedQR"
          class="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4 transition-opacity duration-300"
          @click="closeQRDetail">
          <div class="bg-white rounded-lg w-full max-w-sm overflow-hidden" @click.stop>
            <div class="relative">
              <img :src="selectedQR.image" :alt="selectedQR.name" class="w-full h-64 object-contain bg-white p-4" />
              <button
                class="absolute top-2 right-2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-md cursor-pointer !rounded-button hover:bg-gray-100 transition-all duration-200 transform hover:scale-105 active:scale-95"
                @click="closeQRDetail">
                <i class="fas fa-times text-gray-500"></i>
              </button>
            </div>
            <div class="p-4">
              <div class="flex items-center justify-between">
                <h3 class="text-lg font-medium text-gray-800">{{ selectedQR.name }}</h3>
                <span class="bg-blue-500 text-white text-xs px-2 py-1 rounded-full">{{ selectedQR.type }}</span>
                <div class="flex items-center text-gray-500">
                  <i class="fas fa-eye mr-1"></i>
                  <span class="text-sm">{{ selectedQR.views }}</span>
                </div>
              </div>
              <p class="text-sm text-gray-600 mt-2">{{ selectedQR.description }}</p>
              <div class="flex items-center mt-4">
                <img :src="selectedQR.userAvatar" :alt="selectedQR.userName"
                  class="w-8 h-8 rounded-full object-cover" />
                <div class="ml-2">
                  <div class="text-sm font-medium text-gray-800">{{ selectedQR.userName }}</div>
                  <div class="text-xs text-gray-500">{{ selectedQR.time }}</div>
                </div>
                
              </div>
              <div class="flex justify-between mt-4">
              
              </div>
            </div>
          </div>
        </div>
        <div v-if="isLoading" class="text-center py-4 text-gray-500">加载中...</div>
        <div v-if="noMoreData" class="text-center py-4 text-gray-500">没有更多数据了</div>
      </div>
    </div>
  
  </div>
</template>
<script>
import '@fortawesome/fontawesome-free/css/all.min.css'; // Correct import for Font Awesome CSS
export default {
  mounted() {
    // Add meta tag to disable zooming
    const metaViewport = document.createElement('meta');
    metaViewport.name = 'viewport';
    metaViewport.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    document.head.appendChild(metaViewport);

    // Add SEO meta tags
    const metaDescription = document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = '懂行，是有路径的。从加入圈子，认识大v，读专业信息，研究行业报告，帮您更快的成为行业专业人士。';
    document.head.appendChild(metaDescription);

    const metaKeywords = document.createElement('meta');
    metaKeywords.name = 'keywords';
    metaKeywords.content = '二维码, 微信群, 公众号, 个人微信, 分享二维码';
    document.head.appendChild(metaKeywords);

    const urlParams = new URLSearchParams(window.location.search);
    this.seqNo =  urlParams.get('seqNo');

    this.fetchQRCodes().then(() => {
      // Check for 'id' query parameter and display QR code details if found
      const urlParams = new URLSearchParams(window.location.search);
      const qrId = urlParams.get('id');
      
      if (qrId) {
        let qrCode = null;
        for (let i = 0; i < this.qrCodes.length; i++) {
          if (this.qrCodes[i].id+"" === qrId+"") {
            qrCode = this.qrCodes[i];
             break;
          }
        }
        if (qrCode) {
          this.viewQRCode(qrCode);
        }
      }
    });
    document.addEventListener('keydown', this.handleKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
  },
  data() {
    return {
      headerHeight: 176, // Adjust this value based on the height of the header
      activeCategory: 0,
      activeFilter: 0,
      activeTab: 0,
      showPublishOptions: false,
      showUploadDialog: false,
      selectedQR: null,
      isScanning: false,
      longPressTimer: null,
      showValidationErrors: false,
      seqNo:'',
      searchName: '', // New field for search input
      newQRCode: {
        name: '',
        type: '',
        description: '',
        image: null,
        userName: ''
      },
      categories: ['全部','高考', '微信群', '公众号', '个人微信', '交友', '其他'],
      filters: [
        { name: '最新发布', icon: 'fa-clock' },
        { name: '最多浏览', icon: 'fa-eye' }, 
      ],
      publishOptions: [
        { name: '上传二维码', icon: 'fa-upload' },
      ],
      qrCodes: [],
      page: 1, // Current page
      pageSize: 10, // Number of items per page
      isLoading: false, // Loading state
      noMoreData: false, // No more data state
      isFetching: false, // Add a flag to track fetch state
    };
  },
  computed: {
    filteredCategories() {
      return this.categories.filter(category => category !== '全部'); // Exclude "全部"
    }
  },
  methods: {
    async fetchQRCodes() {
      if (this.isLoading || this.noMoreData) return;
      this.isLoading = true;
      const params = new URLSearchParams();
      const urlParams = new URLSearchParams(window.location.search);
      const qrId = urlParams.get('id');
      params.append('start', parseInt((parseInt(this.page,10) - 1) * parseInt(this.pageSize,10), 10));
      params.append('pageSize', parseInt(this.pageSize, 10));
      if(qrId){
        params.append('id', qrId);
      }else{
        if (this.searchName) {
          params.append('name', this.searchName);
        }
        if (this.activeCategory !== 0) {
          params.append('type', this.categories[this.activeCategory]);
        }
        // Add sorting parameter based on the selected filter
        if (this.activeFilter === 0) {
          params.append('sort', 'time'); // "最新发布"
        } else if (this.activeFilter === 1) {
          params.append('sort', 'views'); // "浏览最多"
        }
      }
      
      try {
        const response = await fetch(`/api/qr-info/query?${params.toString()}`, { method: 'GET' });
        const data = await response.json();
        if (data && data.length > 0) {
          this.qrCodes = [...this.qrCodes, ...data];
          this.page += 1;
        } else {
          this.noMoreData = true;
        }
      } catch (error) {
        console.error('Failed to fetch QR codes:', error);
      } finally {
        this.isLoading = false;
      }
    },
    handleScroll() {
      const scrollContainer = document.querySelector('.content-area');
      if (!scrollContainer) return;

      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

      // console.log('Scroll Top:', scrollTop);
      // console.log('Scroll Height:', scrollHeight);
      // console.log('Client Height:', clientHeight);

      // Ensure scrolling is only handled when content overflows
      if (scrollTop + clientHeight >= scrollHeight - 10 && !this.isFetching) {
        this.isFetching = true; // Set fetching flag
        this.fetchQRCodes().finally(() => {
          this.isFetching = false; // Reset fetching flag after fetch completes
        });
      }
    },
    setActiveCategory(index) {
      this.activeCategory = index;
      this.resetPagination();
    },
    setActiveFilter(index) {
      this.activeFilter = index;
      this.resetPagination();
    },
    handleSearchInput() {
      this.resetPagination();
    },
    resetPagination() {
      this.qrCodes = [];
      this.page = 1;
      this.noMoreData = false;
      this.fetchQRCodes();
    },
    selectPublishOption(index) {
      if (this.publishOptions[index].name === '上传二维码') {
        this.showUploadDialog = true;
      }
      this.showPublishOptions = false;
    },
    closeUploadDialog() {
      this.showUploadDialog = false;
      this.resetNewQRCode();
    },
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = () => {
          const img = new Image();
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const maxWidth = 800; // Set a maximum width for resizing
            const maxHeight = 800; // Set a maximum height for resizing
            let width = img.width;
            let height = img.height;

            // Resize the image while maintaining aspect ratio
            if (width > maxWidth || height > maxHeight) {
              if (width > height) {
                height = Math.round((height * maxWidth) / width);
                width = maxWidth;
              } else {
                width = Math.round((width * maxHeight) / height);
                height = maxHeight;
              }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // Compress the image to ensure it's under 20KB
            let quality = 0.8; // Initial quality
            let compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            while (compressedDataUrl.length > 20 * 1024 && quality > 0.1) {
              quality -= 0.05; // Reduce quality in smaller steps
              compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
            }

            this.newQRCode.image = compressedDataUrl; // Assign the compressed image
          };
          img.src = reader.result;
        };
        reader.readAsDataURL(file);
      }
    },
    async submitQRCode() {
      this.showValidationErrors = true;
      if (
        this.newQRCode.name &&
        this.newQRCode.type &&
        this.newQRCode.description &&
        this.newQRCode.image &&
        this.newQRCode.userName
      ) {
        try {
          const response = await fetch('/api/qr-info/add', { // Updated URL
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(this.newQRCode),
          });
          if (response.ok) {
            console.log('二维码上传成功');
            this.closeUploadDialog();
            this.resetPagination(); // Refresh the QR codes list
          } else {
            console.error('二维码上传失败');
          }
        } catch (error) {
          console.error('提交二维码时出错:', error);
        }
      }
    },
    resetNewQRCode() {
      this.newQRCode = {
        name: '',
        type: '',
        description: '',
        image: null,
        userName: '',
      };
    },
    async viewQRCode(qrCode) {
      this.selectedQR = qrCode;

      // Update the URL to include the QR code ID
      if (window.history.pushState) {
        const url = new URL(window.location.href);
        url.searchParams.set('id', qrCode.id);
        window.history.pushState({}, '', url.toString());
      }

      // Dynamically set the page title
      document.title = `分享神器(懂行的人都用)`;

      // Dynamically set the meta description
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = `${qrCode.userName}向您分享了${qrCode.name},邀请您参加本${qrCode.type},用于${qrCode.description}。`;
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = qrCode.description;
      // wx.miniProgram.postMessage({ data: {foo: 'bar'} })
      // window.parent.postMessage({ title: '分享神器(懂行的人都用)',description:`${qrCode.userName}向您分享了${qrCode.name},邀请您参加本${qrCode.type},用于${qrCode.description}。`}, '*');

      try {
        await fetch('/api/qr-info/increment-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id: qrCode.id,seqNo:this.seqNo,description:`${qrCode.userName}向您分享了${qrCode.name},邀请您参加本${qrCode.type},用于${qrCode.description}。`,type:document.title}),
        });
      } catch (error) {
        console.error('Failed to increment views:', error);
      }
    },
    closeQRDetail() {
      this.selectedQR = null;
      this.cancelLongPress();

      // Remove the 'id' parameter from the URL
      if (window.history.pushState) {
        const url = new URL(window.location.href);
        url.searchParams.delete('id');
        window.history.pushState({}, '', url.toString());
      }

      // Reset the page title and meta description
      document.title = '懂行 | 从认识开始';
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.content = '懂行，是有路径的。从加入圈子，认识大v，读专业信息，研究行业报告，帮您更快的成为行业专业人士。';
      }
      // window.parent.postMessage({ title: '懂行 | 从认识开始',description:'懂行，是有路径的。从加入圈子，认识大v，读专业信息，研究行业报告，帮您更快的成为行业专业人士。'}, '*');
      if(this.qrCodes.length < 2){
        this.fetchQRCodes(); // Fetch data after closing the detail page
      }
    },
    handleKeyDown(event) {
      if (event.key === 'Escape' && this.selectedQR) {
        this.closeQRDetail();
      }
    },
    startLongPress() {
      this.longPressTimer = setTimeout(() => {
        this.scanQRCode();
      }, 800); // 长按800毫秒触发识别
    },
    cancelLongPress() {
      if (this.longPressTimer) {
        clearTimeout(this.longPressTimer);
        this.longPressTimer = null;
      }
    },
    togglePublishOptions() {
      this.showPublishOptions = !this.showPublishOptions;
    },
    closePublishOptions() {
      this.showPublishOptions = false;
    },
  },
};
</script>

<style scoped>
.app-container {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  background-color: #f8f9fa;
  font-family: 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.page-container {
  width: 100%;
  height: 100%;
  overflow-y: auto; /* Added this to make it scrollable */
  padding-bottom: 56px;
}

/* 头部样式 */
.header-fixed {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;
  background-color: #fff;
}

.header-container {
  padding: 10px 16px;
  border-bottom: 1px solid #f0f0f0;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.title {
  font-size: 18px;
  font-weight: bold;
  margin: 0;
}

.notification-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-bar {
  display: flex;
  align-items: center;
  background-color: #f5f5f5;
  border-radius: 18px;
  padding: 8px 12px;
  margin: 8px 0;
}

.search-icon {
  color: #999;
  margin-right: 8px;
}

.search-input {
  flex: 1;
  border: none;
  background: transparent;
  font-size: 14px;
  outline: none;
}

/* 发现页面内容样式 */
.content-container {
  padding: 16px;
  /* margin-top: 100px; */
}

.banner-container {
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
}

.banner-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.function-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.function-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.function-icon-container {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 8px;
}

.function-icon {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.function-name {
  font-size: 12px;
  margin: 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  margin: 0;
}

.section-more {
  font-size: 12px;
  color: #666;
}

.content-cards {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.content-card {
  display: flex;
  background-color: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.card-image-container {
  width: 120px;
  height: 80px;
  flex-shrink: 0;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}

.card-content {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-description {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

.card-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-author {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  margin-right: 6px;
}

.author-name {
  font-size: 12px;
  color: #666;
}

.card-stats {
  display: flex;
  gap: 8px;
}

.stats-item {
  font-size: 12px;
  color: #999;
}

.topic-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.topic-item {
  display: flex;
  background-color: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  padding: 12px;
}

.topic-image-container {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
  margin-right: 12px;
}

.topic-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
}

.topic-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.topic-title {
  font-size: 14px;
  font-weight: bold;
  margin: 0 0 6px 0;
}

.topic-description {
  font-size: 12px;
  color: #666;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-grow: 1;
}

.topic-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.topic-tag {
  font-size: 12px;
  color: #4080ff;
  background-color: #e8f3ff;
  padding: 2px 6px;
  border-radius: 4px;
}

.topic-participants {
  font-size: 12px;
  color: #999;
}

/* 消息页面样式 */
.message-tabs {
  display: flex;
  margin-top: 10px;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  font-size: 14px;
  color: #666;
  position: relative;
}

.tab-item.active {
  color: #4080ff;
  font-weight: bold;
}

.tab-item.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background-color: #4080ff;
  border-radius: 3px;
}

.header-actions {
  display: flex;
  align-items: center;
}

.ml-4 {
  margin-left: 16px;
}

.message-list {
  padding-top: 100px;
}

.message-item {
  display: flex;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fff;
}

.message-avatar-container {
  position: relative;
  margin-right: 12px;
}

.message-avatar {
  width: 50px;
  height: 50px;
  border-radius: 8px;
  object-fit: cover;
}

.unread-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #ff4d4f;
  color: #fff;
  font-size: 10px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.message-content {
  flex: 1;
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;
}

.message-sender {
  font-size: 14px;
  font-weight: bold;
  margin: 0;
}

.message-time {
  font-size: 12px;
  color: #999;
}

.message-preview {
  font-size: 13px;
  color: #666;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 我的页面样式 */
.profile-header {
  position: relative;
  padding-top: 150px;
  margin-bottom: 16px;
}

.profile-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  z-index: 1;
}

.profile-bg-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-info {
  position: relative;
  z-index: 2;
  background-color: #fff;
  border-radius: 16px 16px 0 0;
  margin-top: -20px;
  padding: 0 16px 16px;
  display: flex;
  flex-wrap: wrap;
}

.profile-avatar-container {
  margin-top: -40px;
  margin-right: 16px;
}

.profile-avatar {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.profile-user-info {
  flex: 1;
  padding-top: 16px;
}

.profile-username {
  font-size: 18px;
  font-weight: bold;
  margin: 0 0 4px 0;
}

.profile-bio {
  font-size: 13px;
  color: #666;
  margin: 0;
}

.profile-actions {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.edit-profile-btn {
  background-color: #f5f5f5;
  color: #333;
  border: none;
  padding: 6px 12px;
  font-size: 13px;
  border-radius: 16px;
}

.profile-stats {
  display: flex;
  background-color: #fff;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

.stat-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.profile-content {
  padding: 0 16px 16px;
}

.section-card {
  background-color: #fff;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.section-card-header {
  display: flex;
  align-items: center;
  padding: 16px;
}

.section-icon {
  color: #4080ff;
  font-size: 18px;
  margin-right: 12px;
}

.section-title {
  flex: 1;
  font-size: 15px;
  font-weight: 500;
}

.section-arrow {
  color: #ccc;
}

.recent-scans {
  margin-top: 24px;
}

.recent-scan-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.recent-scan-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.scan-image-container {
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}

.scan-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scan-name {
  font-size: 12px;
  margin: 0 0 4px 0;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.scan-time {
  font-size: 10px;
  color: #999;
  margin: 0;
  text-align: center;
}

/* 底部导航栏 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  width: 100%;
  height: 56px;
  background-color: #fff;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-top: 1px solid #f0f0f0;
  z-index: 100;
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
}

.nav-item.active {
  color: #4080ff;
}

.nav-label {
  font-size: 12px;
  margin-top: 4px;
}


input[type="text"]:focus {
  outline: none;
  box-shadow: none;
}

.category-tabs::-webkit-scrollbar {
  display: none;
}

.category-tabs {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}

.transition-opacity {
  animation: fadeIn 0.3s ease-in-out;
}

.opacity-0 {
  opacity: 0;
}

@media (min-width: 768px) {
  .content-area {
    padding-top: 64px;
  }
  .qr-card {
    transition: transform 0.2s ease-in-out;
  }
  .qr-card:hover {
    transform: scale(1.05);
  }
}
</style>
